import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
import { LAYOUT_TYPE } from "@/views/drag-drop-pages/static/layout-type";
import {
  DROP_POSITION,
  ROW_DROP_POSITIONS
} from "@/views/drag-drop-pages/static/drop-position";
import DropPoints from "@/views/drag-drop-pages/components/builder/DropPoints";
import "@/styles/components/main.scss";

/**
 * Utilty class to display drop points and add/move elements
 */
class CanvasUtil {
  constructor() {
    this.referenceComponents = {};
    this.parents = {};
    this.componentPrefix = "component";
    this.dropPointPrefix = "drop-points";
    this.attributeName = "component-id";
  }
  reset() {
    this.parents = {};
    this.referenceComponents = {};
    this.dropPosition = null;
  }
  displayDropzoneLines(root, mainLayout) {
    const parent = root.$refs[mainLayout];
    this.reset();
    this.addDropzoneLines(parent, parent, mainLayout, 0);
  }
  generateId() {
    return this.componentPrefix + "-" + uuidv4();
  }
  generateDropPointId(componentId) {
    return this.dropPointPrefix + componentId.split(this.componentPrefix)[1];
  }
  addDropzoneLines(parentComponent, refComponent, mainLayout, level, index) {
    if (
      refComponent.allowDropzones &&
      !refComponent.$el.classList.contains("m-drag_start")
    ) {
      const isRoot =
        parentComponent.id === refComponent.id &&
        parentComponent.id === mainLayout;
      let el = refComponent.$el;
      if (!el.getAttribute(this.attributeName)) {
        el.setAttribute(this.attributeName, this.generateId());
      }
      const componentId = el.getAttribute(this.attributeName);
      const InstanceClass = Vue.extend(DropPoints);
      const dropPoint = new InstanceClass();
      dropPoint.id = this.generateDropPointId(componentId);
      dropPoint.componentId = componentId;
      dropPoint.parentLayoutId = parentComponent.id;
      const totalChildNodes = parentComponent.$refs.canvas.childNodes
        ? parentComponent.$refs.canvas.childNodes.length
        : 0;
      dropPoint.data = {
        layout: refComponent.type,
        parentLayout: parentComponent.type,
        position: index,
        totalNodes: totalChildNodes,
        parentId: parentComponent.id,
        depth: level,
        mainLayoutId: mainLayout
      };

      const show = {};
      if (
        parentComponent.id === refComponent.id &&
        !refComponent.$refs.canvas.childNodes.length
      ) {
        // root layout, show only center line since canvas is empty
        show.center = true;
      } else {
        if (refComponent.type === LAYOUT_TYPE.ROW) {
          show.top = true;
          show.bottom = true;
        } else if (refComponent.type === LAYOUT_TYPE.COLUMN) {
          show.left = true;
          show.right = true;
        } else if (
          this.isNotRootElement(parentComponent, refComponent, mainLayout)
        ) {
          // can be section or just an element
          show.center =
            refComponent.$refs.canvas &&
            !refComponent.$refs.canvas.childNodes.length;
          if (parentComponent.type === LAYOUT_TYPE.ROW) {
            show.left = true;
            show.right = true;
            if (refComponent.type !== LAYOUT_TYPE.COLUMN) {
              show.top = true;
              show.bottom = true;
            }
          } else {
            // parent has only one element
            show.top = true;
            show.bottom = true;
            show.left = true;
            show.right = true;
          }
        }
      }
      dropPoint.isRoot = isRoot;
      dropPoint.show = show;
      const onDropElement = (ev, data) => {
        this.dropPosition = data;
        refComponent.onCanvasDropzone(ev);
      };
      dropPoint.$on("on-drop-points", onDropElement);
      dropPoint.$mount();
      dropPoint.$el.setAttribute("id", dropPoint.id);
      el.appendChild(dropPoint.$el);
      refComponent.$refs[dropPoint.id] = dropPoint;
      this.parents[refComponent.id] = parentComponent;
      this.referenceComponents[dropPoint.componentId] = refComponent;
    }
    if (refComponent.$refs.canvas) {
      level++;
      let i = 0;
      for (const childNode of refComponent.$refs.canvas.childNodes) {
        const child = refComponent.$refs[childNode.getAttribute("id")];
        if (child instanceof Vue && child.$options.name !== "drop-points") {
          this.addDropzoneLines(refComponent, child, mainLayout, level, i++);
        }
      }
    }
  }
  isNotRootElement(parent, ref, mainLayout) {
    return (
      parent.id !== mainLayout ||
      (parent.id === mainLayout && ref.id !== mainLayout)
    );
  }
  hideDropzoneLines(rootComponent) {
    this.removeDropzoneLines(rootComponent);
  }
  removeDropzoneLines(component) {
    if (component.allowDropzones) {
      component.removeDropzoneLines(this.dropPointPrefix);
    }
    for (const [, child] of Object.entries(component.$refs)) {
      if (child instanceof Vue && child.allowDropzones) {
        this.removeDropzoneLines(child);
      }
    }
  }
  appendComponent(instance, root, isMove) {
    this.append(instance, root);
    // Reset, no longer needed
    this.dropPosition = null;
    dragAndDrop.triggerCanvasUpdate(isMove);
  }

  append(instance, root) {
    let refComponent = root; // root will be valid only from main page canvas.
    if (this.dropPosition) {
      refComponent = this.referenceComponents[this.dropPosition.componentId];
    }
    if (
      !this.dropPosition ||
      this.dropPosition.position === DROP_POSITION.CENTER
    ) {
      // Not this.dropPosition means this is the first element
      // adding into center of canvas
      refComponent.$refs.canvas.appendChild(instance.$el);
      refComponent.$refs[instance.id] = instance;
    } else {
      this.addComponent(refComponent, instance, this.dropPosition);
    }
  }

  addComponent(refComponent, instance, dropData) {
    const parentComponent = this.parents[refComponent.id];
    const parentComponentHasNoChildren = !parentComponent.$refs.canvas
      .childNodes.length;
    const dropDataHasNoPosition = !dropData?.position;

    if (
      parentComponent.type === LAYOUT_TYPE.SECTION &&
      parentComponentHasNoChildren &&
      dropDataHasNoPosition
    ) {
      // it will simply append to the canvas of parentComponent
      this.insertComponent(refComponent, parentComponent, instance, dropData);
      return;
    }
    if (parentComponent.type === LAYOUT_TYPE.SECTION) {
      // parentComponent has at least one child element
      // set parent type for subsequent additions
      const sectionType = ROW_DROP_POSITIONS.includes(dropData.position)
        ? LAYOUT_TYPE.ROW
        : LAYOUT_TYPE.COLUMN;
      this.wrapAndInsertComponent(
        refComponent,
        parentComponent,
        instance,
        dropData,
        sectionType
      );
    } else if (parentComponent.type === LAYOUT_TYPE.ROW) {
      if (ROW_DROP_POSITIONS.includes(dropData.position)) {
        // add element to either left or right
        this.insertComponent(refComponent, parentComponent, instance, dropData);
      } else {
        // add column
        this.wrapAndInsertComponent(
          refComponent,
          parentComponent,
          instance,
          dropData,
          LAYOUT_TYPE.COLUMN
        );
      }
    } else if (parentComponent.type === LAYOUT_TYPE.COLUMN) {
      if (
        dropData.position === DROP_POSITION.TOP ||
        dropData.position === DROP_POSITION.BOTTOM
      ) {
        // add element to either top or bottom
        this.insertComponent(refComponent, parentComponent, instance, dropData);
      } else {
        // add row
        this.wrapAndInsertComponent(
          refComponent,
          parentComponent,
          instance,
          dropData,
          LAYOUT_TYPE.ROW
        );
      }
    }
  }

  wrapAndInsertComponent(
    refComponent,
    parentComponent,
    instance,
    dropData,
    sectionType
  ) {
    const section = this.getSection();
    section.type = sectionType;
    // remove current refComponent reference
    delete parentComponent.$refs[refComponent.id];
    parentComponent.$refs.canvas.replaceChild(section.$el, refComponent.$el);
    parentComponent.$refs[section.id] = section;
    //  add refComponent to new section
    section.$refs.canvas.append(refComponent.$el);
    section.$refs[refComponent.id] = refComponent;
    this.insertComponent(refComponent, section, instance, dropData);
  }

  getSection() {
    const data = {
      props: {
        card: false
      }
    };
    return dragAndDrop.createDraggableComponent(
      "CardLayout",
      data,
      data.props.card
    );
  }

  insertComponent(refComponent, parentComponent, instance, dropData) {
    const parentCanvas = parentComponent.$refs.canvas;
    const currentSection = refComponent.$el;
    if (parentComponent.type === LAYOUT_TYPE.ROW) {
      if (dropData.position === DROP_POSITION.LEFT) {
        parentCanvas.insertBefore(instance.$el, currentSection);
      } else {
        parentCanvas.append(instance.$el);
      }
    } else {
      if (dropData.position === DROP_POSITION.TOP) {
        parentCanvas.insertBefore(instance.$el, currentSection);
      } else {
        parentCanvas.append(instance.$el);
      }
    }
    parentComponent.$refs[instance.id] = instance;
    parentComponent.updateRowLayout();
  }

  moveComponent(rootComponent, mainLayout, instance, refComponent) {
    this.appendComponent(instance, refComponent, true);
    const layout = rootComponent.$refs[mainLayout];
    layout.normalizeElements();
    for (const [, component] of Object.entries(rootComponent.$refs)) {
      if (component instanceof Vue && component.updateRowLayout) {
        component.updateRowLayout();
      }
    }
  }

  iniCanvas(root, jsonObj, appId, mainLayout) {
    const parentComponent = root.$refs[mainLayout];
    if (jsonObj?.display) {
      this.buildComponent(parentComponent, jsonObj.display, appId);
    }
  }
  buildComponent(parentComponent, jsonObj, appId) {
    if (Array.isArray(jsonObj)) {
      for (const obj of jsonObj) {
        this.buildSingleElement(parentComponent, obj, appId);
      }
    } else {
      this.buildSingleElement(parentComponent, jsonObj, appId);
    }
  }

  buildSingleElement(parentComponent, jsonObj, appId) {
    const deserializedInstance = dragAndDrop.deserializeComponent(
      jsonObj,
      appId
    );
    if (deserializedInstance?.instance) {
      this.append(deserializedInstance.instance, parentComponent);
      this.buildElementContent(deserializedInstance, jsonObj, appId);
    } else {
      console.error(
        "Error - deserialize() function not available for element: ",
        jsonObj.element
      );
    }
  }

  buildElementContent(deserializedInstance, jsonObj, appId) {
    if (jsonObj.body) {
      this.buildComponent(
        deserializedInstance.instance,
        deserializedInstance.content.body,
        appId
      );
    } else if (jsonObj.children) {
      this.buildComponent(
        deserializedInstance.instance,
        deserializedInstance.content.children,
        appId
      );
    }
  }
}

export default new CanvasUtil();
